<template>
    <div class="hue-page homepage" data-hue="periwinkle">
        <header class="bar">
            <span class="wordmark">Sitzplan</span>
            <router-link to="/generator" class="btn btn-sm">Zum Generator <ArrowRight aria-hidden="true" /></router-link>
        </header>

        <main class="page landing">
            <div class="pitch">
                <h1 class="title">Sitzplan.ch <span class="name">Zufällige Sitzpläne und Gruppenarbeiten fürs Klassenzimmer</span></h1>
                <p class="lede">Richte dein Zimmer ein, wähle die Klasse und mische. Gratis, ohne Konto und ohne Werbung: deine Klassen und Zimmer bleiben auf deinem Gerät.</p>
                <div class="cta">
                    <button type="button" class="btn btn-primary" @click="goToTool">Los gehts! <ArrowRight aria-hidden="true" /></button>
                </div>
            </div>

            <!-- A small room that really shuffles, as a taste of the tool -->
            <div class="leaf example">
                <div class="example-head">
                    <h2 class="leaf-title">Klasse 26Gx</h2>
                    <p class="example-where">Zimmer 1.55 · 9 Schüler:innen · 9 Tische</p>
                </div>
                <div class="anim-container" aria-label="Beispiel eines Sitzplans">
                    <div class="cell" v-for="(cell, index) in cells" :key="index"
                        :class="{ available: cell.available, picked: cell.name && cell.name === pickedName }"
                        :style="{ animationDelay: `${cell.order * 60}ms` }">
                        <span class="seat-name" :key="cell.name" v-if="cell.name">{{ cell.name }}</span>
                    </div>
                </div>
                <p class="example-summary" role="status">{{ pickedName ? `Dran ist ${pickedName}.` : 'Alle 9 haben einen Platz.' }}</p>
                <div class="cta">
                    <button type="button" class="btn btn-sm" @click="shuffleExample"><Dices aria-hidden="true" /> Neu mischen</button>
                    <button type="button" class="btn btn-sm" @click="pickExample"><Hand aria-hidden="true" /> Zufällig auswählen</button>
                </div>
            </div>

            <section class="features">
                <h2 class="features-title">Was Sitzplan für dich macht</h2>
                <ul class="feature-grid">
                    <li class="leaf tag" v-for="feature in features" :key="feature.title">
                        <span class="t-icon"><component :is="feature.icon" aria-hidden="true" /></span>
                        <h3 class="tag-header">{{ feature.title }}</h3>
                        <p>{{ feature.text }}</p>
                    </li>
                </ul>
                <div class="closing">
                    <p>Ein Projekt von mir als Lehrperson für Lehrpersonen. Fragen und Anregungen nehme ich gern per <a href="mailto:claudio.brasser@gymneufeld.ch">E-Mail</a> entgegen.</p>
                    <button type="button" class="btn btn-primary" @click="goToTool">Los gehts! <ArrowRight aria-hidden="true" /></button>
                </div>
            </section>
        </main>

        <SiteFooter />
    </div>
  </template>

  <script>
import { ArrowRight, Dices, Hand, HardDrive, Printer, Split, Users } from '@lucide/vue';
import SiteFooter from './components/ui/SiteFooter.vue';
import { shuffle } from './seating.js';

// Where the tables of the example room stand, row by row
const EXAMPLE_ROOM = [
    1, 1, 0, 1, 1,
    1, 1, 0, 1, 1,
    0, 0, 1, 0, 0,
];
const EXAMPLE_NAMES = ['Anna', 'Max', 'Luisa', 'Noah', 'Mia', 'Elias', 'Lena', 'Jonas', 'Sara'];

  export default {
    name: 'Home',
    components: {
        ArrowRight, Dices, Hand, HardDrive, Printer, Split, Users, SiteFooter,
    },
    data() {
        return {
            names: shuffle(EXAMPLE_NAMES),
            pickedName: null,
            features: [
                {
                    icon: 'Dices',
                    title: 'Zufällige Sitzordnung',
                    text: 'Ein Klick verteilt die Klasse neu auf die Tische deines Zimmers. Passt etwas nicht, tauschst du zwei Plätze von Hand.',
                },
                {
                    icon: 'Users',
                    title: 'Gruppenarbeit',
                    text: 'Teams nach Grösse oder Anzahl, in Sekunden gebildet. Wer fehlt, bleibt draussen.',
                },
                {
                    icon: 'Hand',
                    title: 'Zufällig auswählen',
                    text: 'Wer ist dran? Der Zufall entscheidet, und alle kommen einmal an die Reihe, bevor jemand zweimal muss.',
                },
                {
                    icon: 'Split',
                    title: 'Trennen und zusammensetzen',
                    text: 'Wer besser nicht nebeneinander sitzt, wird getrennt, wer zusammengehört, bleibt zusammen. Einzelne Plätze lassen sich fixieren, ganze Sitzordnungen fest speichern.',
                },
                {
                    icon: 'HardDrive',
                    title: 'Gratis und privat',
                    text: 'Gratis und werbefrei. Deine Klassen und Zimmer werden lokal auf deinem Gerät gespeichert, nicht auf einem Server.',
                },
                {
                    icon: 'Printer',
                    title: 'Zeigen, drucken, sichern',
                    text: 'Der Sitzplan bleibt gespeichert, füllt im Vollbild den Beamer und lässt sich drucken. Eine Sicherungsdatei nimmt alles auf ein anderes Gerät mit.',
                },
            ],
        }
    },
    computed: {
        cells() {
            let seat = 0;
            return EXAMPLE_ROOM.map((available, index) => ({
                available: !!available,
                name: available ? this.names[seat++] : null,
                order: index,
            }));
        }
    },
    methods: {
        goToTool: function() {
            this.$router.push('/generator')
        },
        shuffleExample: function() {
            this.pickedName = null
            this.names = shuffle(EXAMPLE_NAMES)
        },
        pickExample: function() {
            const others = EXAMPLE_NAMES.filter(name => name !== this.pickedName)
            this.pickedName = others[Math.floor(Math.random() * others.length)]
        }
    }
  }
  </script>


  <style scoped>
.landing {
    display: grid;
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
    align-items: center;
    gap: var(--s-7);
    padding-top: var(--s-6);
}

.pitch {
    display: grid;
    gap: var(--s-4);
    justify-items: start;
}

.title {
    font-size: var(--t-display);
    letter-spacing: -0.035em;
    line-height: 0.95;
}

/* What it is sits under the name inside the one h1, so it counts as the page's heading. */
.name {
    display: block;
    margin-top: var(--s-3);
    font-size: var(--t-2xl);
    letter-spacing: -0.015em;
    line-height: 1.1;
}

.lede {
    max-width: 52ch;
    font-size: var(--t-lg);
}

.cta {
    display: flex;
    flex-wrap: wrap;
    gap: var(--s-3);
}

/* ---------------------------------------------------------------- example */

.example {
    display: grid;
    gap: var(--s-4);
    justify-items: start;
    padding: var(--s-5);
}

.example-head {
    display: grid;
    gap: 2px;
}

.example-where {
    font-size: var(--t-sm);
    font-weight: 700;
    color: var(--ink-2);
}

.anim-container {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    grid-auto-rows: 56px;
    gap: 6px;
    width: 100%;
    padding: var(--s-3);
    border: var(--line);
    border-radius: var(--r-control);
    background: var(--hue-wash);
}

.cell {
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 0;
    border: 1px solid var(--hairline);
    border-radius: var(--r-cell);
    transition:
      transform var(--dur) var(--ease),
      box-shadow var(--dur) var(--ease),
      background-color var(--dur) var(--ease);
}

.cell.available {
    border: var(--line);
    background: var(--paper);
    box-shadow: var(--shadow-sm);
}

.cell.picked {
    background: var(--hue);
    transform: translate(-2px, -2px);
    box-shadow: var(--shadow);
}

.seat-name {
    padding: 0 2px;
    font-size: var(--t-sm);
    font-weight: 800;
    overflow: hidden;
    text-overflow: ellipsis;
}

.example-summary {
    font-size: var(--t-lg);
    font-weight: 700;
    line-height: 1.35;
}

@media (prefers-reduced-motion: no-preference) {
    /* The tables are set one after the other, then the names arrive */
    .cell.available {
        animation: set-table var(--dur-slow) var(--ease) backwards;
    }

    .seat-name {
        animation: name-appear var(--dur-slow) var(--ease) backwards;
    }
}

@keyframes set-table {
    from {
        opacity: 0;
        transform: translate(2px, 2px);
        box-shadow: 0 0 0 var(--ink);
    }
}

@keyframes name-appear {
    from {
        opacity: 0;
        transform: translateY(4px);
    }
}

/* --------------------------------------------------------------- features */

.features {
    grid-column: 1 / -1;
    display: grid;
    gap: var(--s-5);
}

.features-title {
    font-size: var(--t-2xl);
}

.feature-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: var(--s-5);
    margin: 0;
    padding: 0;
    list-style: none;
}

.tag {
    display: grid;
    align-content: start;
    gap: var(--s-2);
    padding: var(--s-5);
}

.tag p {
    font-weight: 600;
    color: var(--ink-2);
}

.tag-header {
    font-size: var(--t-lg);
}

.t-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    margin-bottom: var(--s-1);
    border: var(--line);
    border-radius: 50%;
    background: var(--hue);
}

.t-icon svg {
    width: 20px;
    height: 20px;
    stroke-width: 2.5;
}

.closing {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: var(--s-4);
}

.closing p {
    max-width: 62ch;
    font-weight: 700;
}

@media (max-width: 999px) {
    .feature-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 899px) {
    .landing {
        grid-template-columns: minmax(0, 1fr);
        gap: var(--s-6);
    }
}

@media (max-width: 599px) {
    .example,
    .tag {
        padding: var(--s-4);
    }

    .feature-grid {
        grid-template-columns: minmax(0, 1fr);
    }

    .anim-container {
        grid-auto-rows: 48px;
        padding: var(--s-2);
    }

    .seat-name {
        font-size: var(--t-xs);
    }
}
  </style>
